/**
 * TradeFinder: the action-bar entry point for everything order-flow
 * (flow-signals relayed by the engine). The button carries the relay's
 * status dot; it opens a panel anchored under it on desktop and a bottom
 * sheet on narrow screens, the same shell as the Levels panel.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Radar } from 'lucide-react';
import { useFlowSignalsStore, flowStatusDot } from '../../store/flowSignalsStore';
import { computePopoverPosition, SHEET_BREAKPOINT } from '../LevelsToggle';
import TradeFinderPanel from './TradeFinderPanel';
import type { TradeFinderTabProps } from './tabs';
import '../LevelsToggle.css';
import './TradeFinder.css';

const EDGE = 8;
const PANEL_WIDTH = 380;

const isNarrow = () => typeof window !== 'undefined' && window.innerWidth < SHEET_BREAKPOINT;

const DOT_TITLE = {
  connected: 'flow-signals connected',
  offline: 'flow-signals offline',
  disabled: 'flow-signals disabled on the engine',
  unknown: 'flow-signals status unknown',
} as const;

type PopoverPos = ReturnType<typeof computePopoverPosition>;

const TradeFinderToggle: React.FC<TradeFinderTabProps> = ({ symbol, onJumpTo }) => {
  const [open, setOpen] = useState(false);
  const [narrow, setNarrow] = useState(isNarrow);
  const [pos, setPos] = useState<PopoverPos | null>(null);
  const [dragY, setDragY] = useState(0);
  const dragStart = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const dot = useFlowSignalsStore((f) => flowStatusDot(f.status));

  // Status (and live signals) for the dot, for as long as the bar is mounted.
  useEffect(() => {
    useFlowSignalsStore.getState().initialize();
    return () => useFlowSignalsStore.getState().teardown();
  }, []);

  // The recent list needs this symbol's history even with the markers off.
  useEffect(() => {
    if (!open) return;
    return useFlowSignalsStore.getState().watchSymbol(symbol);
  }, [open, symbol]);

  useEffect(() => {
    const onResize = () => setNarrow(isNarrow());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setDragY(0);
  }, []);

  useLayoutEffect(() => {
    if (!open || narrow) return;
    const measure = () => {
      if (!containerRef.current) return;
      setPos(computePopoverPosition(containerRef.current.getBoundingClientRect(), window.innerWidth, window.innerHeight));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open, narrow]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!containerRef.current?.contains(t) && !panelRef.current?.contains(t)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const dragHandlers: React.HTMLAttributes<HTMLDivElement> = {
    onTouchStart: (e) => { dragStart.current = e.touches[0].clientY; },
    onTouchMove: (e) => {
      if (dragStart.current !== null) setDragY(Math.max(0, e.touches[0].clientY - dragStart.current));
    },
    onTouchEnd: () => {
      dragStart.current = null;
      if (dragY > 80) close();
      else setDragY(0);
    },
    onTouchCancel: () => { dragStart.current = null; setDragY(0); },
  };

  const panel = (
    <TradeFinderPanel symbol={symbol} onJumpTo={onJumpTo} onClose={close} mobile={narrow} dragHandlers={dragHandlers} />
  );

  return (
    <div ref={containerRef} className="levels-toggle-container tradefinder-container">
      <button
        type="button"
        className={`action-bar-btn ${open ? 'active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        title={`TradeFinder (${DOT_TITLE[dot]})`}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid="tradefinder-button"
      >
        <Radar size={14} className="action-bar-icon" />
        <span className="action-bar-label">TradeFinder</span>
        <span className={`tradefinder-dot tradefinder-button-dot is-${dot}`} data-testid="tradefinder-status-dot" data-state={dot} />
      </button>

      {open && narrow && createPortal(
        <>
          <div className="levels-sheet-backdrop" onClick={close} />
          <div
            ref={panelRef}
            className="levels-dropdown levels-sheet tradefinder-panel"
            role="dialog"
            aria-label="TradeFinder"
            data-testid="tradefinder-panel"
            style={dragY ? { transform: `translateY(${dragY}px)`, transition: 'none' } : undefined}
          >
            {panel}
          </div>
        </>,
        document.body,
      )}

      {open && !narrow && createPortal(
        <div
          ref={panelRef}
          className="levels-dropdown levels-popover tradefinder-panel"
          role="dialog"
          aria-label="TradeFinder"
          data-testid="tradefinder-panel"
          style={{
            position: 'fixed',
            left: pos?.left ?? EDGE,
            width: pos?.width ?? PANEL_WIDTH,
            maxHeight: pos ? `${pos.maxHeight}px` : '70vh',
            ...(pos?.bottom !== undefined ? { bottom: pos.bottom } : { top: pos?.top ?? 0 }),
            zIndex: 10000,
          }}
        >
          {panel}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default TradeFinderToggle;
